import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Rating,
  CircularProgress,
  useTheme,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Pagination,
  alpha,
  Grid,
  Divider
} from '@mui/material';
import AutocompleteField from 'components/formFields/autocompleteField';
import { Formik, Form } from 'formik';
import StarRateIcon from '@mui/icons-material/StarRate';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LinkIcon from '@mui/icons-material/Link';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FolderIcon from '@mui/icons-material/Folder';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GoogleIcon from '@mui/icons-material/Google';
import { alertInformativo } from '../../../../commons/texts';
import { AlertsInformativos } from '../../../../components/generics/AlertsInformativos';

import { getGoogleBusinessResumenStore, storeReplyGoogleBusinessReview, generateAiResponseReviewStore } from '../../../../store/ratings/thunks';
import { BreadCrumbsAndTitle } from '../../../../components/generics/BreadCrumbsAndTitle';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';
import { mtDividerUpTable } from '../../../../commons';

const getStarRatingValue = (ratingStr: string | number) => {
  if (!ratingStr) return 0;
  const parsed = parseFloat(ratingStr as string);
  if (!isNaN(parsed)) return parsed;
  const starMapping: { [key: string]: number } = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
  return starMapping[ratingStr as string] || 0;
};

export const Resumen = () => {
  const dispatch = useDispatch<any>();
  const theme = useTheme();
  const [data, setData] = useState<any>(null);
  const [selectedLocation, setSelectedLocation] = useState<any>(null);
  const { google_bussinesses_list_for_select } = useSelector((state: any) => state.ratingsDevices);
  // const [loading, setLoading] = useState<boolean>(true);
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState<any>(null);

  const [replyText, setReplyText] = useState<string>('');
  const [isGeneratingIA, setIsGeneratingIA] = useState(false);
  const { handleResponse, ResponseComponents } = useResponseHandler();

  useEffect(() => {
    if (selectedReview) {
      if (selectedReview.reviewReply?.comment) {
        setReplyText(selectedReview.reviewReply.comment);
      } else {
        setReplyText('');
      }
    }
  }, [selectedReview]);

  const handleSendReply = async () => {
    if (!selectedReview || !replyText.trim()) return;
    const response = await dispatch(storeReplyGoogleBusinessReview(selectedReview.reviewId, replyText));
    await handleResponse(response, selectedReview.reviewReply ? 'actualizar respuesta' : 'enviar respuesta a Google', true);
  };

  const handleGenerateWithIA = async () => {
    if (!selectedReview) return;
    setIsGeneratingIA(true);

    const payload = {
        authorName: selectedReview.reviewer?.displayName || "",
        rating: getStarRatingValue(selectedReview.starRating) || 0,
        review: selectedReview.comment ? selectedReview.comment.split('(Translated by Google)')[0].trim() : "",
        businessName: data?.googleLocation?.name || data?.googleLocation?.title || "", 
        language: "es"
    };

    const response = await dispatch(generateAiResponseReviewStore(payload));
    setIsGeneratingIA(false);
    
    if (response?.ok && response?.data?.response) {
         setReplyText(response.data.response);
    } else {
         await handleResponse(response, 'Generar con IA', true);
    }
  };


  useEffect(() => {
    const fetchData = async () => {
      // setLoading(true);
      const response = await dispatch(getGoogleBusinessResumenStore(selectedLocation?.value));
      if (response && response.ok && response.data) {
        setData(response.data);
        if (response.data.resenas && response.data.resenas.length > 0) {
          setSelectedReview(response.data.resenas[0]);
        }
      }
      // setLoading(false);
    };
    fetchData();
  }, [dispatch, selectedLocation]);

  // if (loading) {
  //   return (
  //     <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
  //       <CircularProgress color="primary" />
  //     </Box>
  //   );
  // }

  if (!data) {
    return (
      <Box textAlign="center" py={5}>
        <Typography color="text.secondary">No se pudo cargar el resumen.</Typography>
      </Box>
    );
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  };

  const MetricCard = ({ title, value, tendencyData, icon, iconBgColor, isRating = false }: any) => {
    const isPositive = tendencyData?.esPositivo;
    const trendColor = isPositive ? '#34A853' : '#EA4335';
    const TrendIcon = tendencyData?.tendencia >= 0 ? ArrowUpwardIcon : ArrowDownwardIcon;

    return (
      <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: iconBgColor, width: 48, height: 48 }}>{icon}</Avatar>
            <Box>
              <Typography variant="body2" color="text.secondary" fontWeight="500">{title}</Typography>
              <Typography variant="h4" fontWeight="800" sx={{ mt: 0.5 }}>{value}</Typography>
            </Box>
          </Box>
          {isRating && (
            <Box mb={2}>
              <Rating value={parseFloat(value)} precision={0.1} readOnly size="small" sx={{ color: '#FBBC04' }} />
            </Box>
          )}
          <Box mt="auto" display="flex" alignItems="center" gap={0.5}>
            <TrendIcon sx={{ color: trendColor, fontSize: 16 }} />
            <Typography variant="caption" sx={{ color: trendColor, fontWeight: 'bold' }}>
              {tendencyData?.textoTendencia}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const getStatusChip = (estado: string) => {
    switch (estado) {
      case 'Respondida':
        return <Chip label="Respondida" size="small" sx={{ bgcolor: alpha('#34A853', 0.1), color: '#34A853', fontWeight: 'bold', border: '1px solid #34A853' }} />;
      case 'Sin responder':
        return <Chip label="Sin responder" size="small" sx={{ bgcolor: alpha('#FBBC04', 0.1), color: '#F9A825', fontWeight: 'bold', border: '1px solid #F9A825' }} />;
      case 'Crítica':
        return <Chip label="Crítica" size="small" sx={{ bgcolor: alpha('#EA4335', 0.1), color: '#EA4335', fontWeight: 'bold', border: '1px solid #EA4335' }} />;
      default:
        return <Chip label={estado} size="small" />;
    }
  };



  const allReviews = data.resenas || [];
  let filteredReviews = allReviews;
  if (tabValue === 1) filteredReviews = allReviews.filter((r: any) => r.estado === 'Sin responder');
  if (tabValue === 2) filteredReviews = allReviews.filter((r: any) => getStarRatingValue(r.starRating) <= 3);
  if (tabValue === 3) filteredReviews = allReviews.filter((r: any) => getStarRatingValue(r.starRating) >= 4);

  const paginatedReviews = filteredReviews.slice((page - 1) * 10, page * 10);

  return (
    <Box sx={{ pb: 4, px: 2 }}>
      {ResponseComponents}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
            <BreadCrumbsAndTitle
              title="Resumen" 
              mainModule="Reputación Online" 
              parent="Resumen"
              action="" 
              icon="Assessment"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
               <AlertsInformativos type={'info'} title={'info'} content={alertInformativo.resumenReputacion} /> 
            </Typography>
        </Box>
      </Box>
      <Divider sx={{ marginBottom: mtDividerUpTable }}/>

      {/* 5 METRIC CARDS */}
      <Grid container spacing={3} mb={4}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard title="Calificación promedio" value={data.calificacionPromedio?.valor} tendencyData={data.calificacionPromedio} icon={<StarRateIcon />} iconBgColor="#4A148C" isRating />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard title="Nuevas reseñas" value={data.nuevasResenas?.valor} tendencyData={data.nuevasResenas} icon={<ChatBubbleOutlineIcon />} iconBgColor="#34A853" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard title="Reseñas críticas (1-2★)" value={data.resenasCriticas?.valor} tendencyData={data.resenasCriticas} icon={<SentimentVeryDissatisfiedIcon />} iconBgColor="#EA4335" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard title="Tiempo prom. respuesta" value={`${data.tiempoPromedioRespuesta?.valor} h`} tendencyData={data.tiempoPromedioRespuesta} icon={<AccessTimeIcon />} iconBgColor="#FBBC04" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <MetricCard title="Reseñas respondidas" value={`${data.resenasRespondidas?.valor}%`} tendencyData={data.resenasRespondidas} icon={<CheckCircleOutlineIcon />} iconBgColor="#1976D2" />
        </Grid>
      </Grid>

      {/* BOTTOM SECTION: TABLE + RIGHT PANEL */}
      <Grid container spacing={3}>
        {/* LEFT COLUMN: TABS & TABLE */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 'bold' } }}
            >
              <Tab label={`Todas (${allReviews.length})`} />
              <Tab label={`Sin responder (${allReviews.filter((r: any) => r.estado === 'Sin responder').length})`} />
              <Tab label={`Críticas (${allReviews.filter((r: any) => getStarRatingValue(r.starRating) <= 3).length})`} />
              <Tab label={`Positivas (${allReviews.filter((r: any) => getStarRatingValue(r.starRating) >= 4).length})`} />
            </Tabs>
            <Box display="flex" gap={2} alignItems="center">
              <Box sx={{ width: 250 }}>
                <Formik 
                  initialValues={{ location: selectedLocation || null }} 
                  onSubmit={() => {}}
                  enableReinitialize
                >
                  <Form>
                    <AutocompleteField  
                        options={google_bussinesses_list_for_select || []}
                        name="location"
                        label="Filtrar por negocio"
                        onChange={(event: any, newValue: any) => setSelectedLocation(newValue)}
                    />
                  </Form>
                </Formik>
              </Box>
              <TextField
                placeholder="Buscar reseñas..."
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                }}
                sx={{ bgcolor: 'white', borderRadius: 1 }}
              />
              <Button variant="outlined" startIcon={<FilterListIcon />} sx={{ bgcolor: 'white', color: 'text.primary', borderColor: 'divider', height: 40 }}>
                Filtros
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Table sx={{ minWidth: 800 }}>
              <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Calificación</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', width: '30%' }}>Comentario</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Estado</TableCell>
                  <TableCell align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedReviews.length > 0 ? (
                  paginatedReviews.map((row: any, index: number) => {
                    const numericRating = getStarRatingValue(row.starRating) || 0;
                    const dateObj = new Date(row.createTime);
                    const isSelected = selectedReview?.reviewId === row.reviewId;

                    return (
                      <TableRow 
                        key={row.reviewId || index} 
                        hover 
                        onClick={() => setSelectedReview(row)}
                        sx={{ 
                          cursor: 'pointer',
                          bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'inherit',
                          '& td': { borderBottom: '1px solid #f0f0f0' }
                        }}
                      >
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar src={row.reviewer?.profilePhotoUrl} sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main, fontSize: '0.9rem' }}>
                              {row.reviewer?.displayName?.charAt(0)}
                            </Avatar>
                            <Typography variant="body2" fontWeight="bold">
                              {row.reviewer?.displayName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Rating value={numericRating} readOnly size="small" sx={{ color: '#FBBC04' }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', color: row.comment ? 'text.primary' : 'text.secondary', fontStyle: row.comment ? 'normal' : 'italic' }}>
                            {row.comment || 'Sin comentario'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{dateObj.toLocaleDateString('es-ES')}</Typography>
                          <Typography variant="caption" color="text.secondary">{dateObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(row.estado)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small">
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">No hay reseñas que coincidan con este filtro.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              Mostrando {(page - 1) * 10 + 1} a {Math.min(page * 10, filteredReviews.length)} de {filteredReviews.length} reseñas
            </Typography>
            <Pagination 
              count={Math.ceil(filteredReviews.length / 10)} 
              page={page} 
              onChange={(e, value) => setPage(value)} 
              color="primary" 
              shape="rounded"
            />
          </Box>
        </Grid>

        {/* RIGHT COLUMN: DETAIL PANEL */}
        <Grid size={{ xs: 12, lg: 4 }}>
          

          {selectedReview ? (
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <CardContent sx={{ p: 0 }}>
                {/* Reviewer Details */}
                <Box p={3} pb={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box display="flex" gap={2}>
                      <Avatar 
                        src={selectedReview.reviewer?.profilePhotoUrl} 
                        sx={{ width: 48, height: 48, bgcolor: theme.palette.primary.main, color: 'white' }}
                      >
                        {selectedReview.reviewer?.displayName?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                          {selectedReview.reviewer?.displayName}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <StarRateIcon sx={{ color: '#FBBC04', fontSize: 16 }} />
                          <Typography variant="caption" color="text.secondary">Local Guide • 12 reseñas</Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box textAlign="right">
                      <Rating value={getStarRatingValue(selectedReview.starRating) || 0} readOnly size="small" sx={{ color: '#FBBC04' }} />
                      <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                        {new Date(selectedReview.createTime).toLocaleDateString('es-ES')} <br/>
                        {new Date(selectedReview.createTime).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body1" mt={3} mb={2} sx={{ lineHeight: 1.6, color: 'text.primary' }}>
                    {selectedReview.comment || <span style={{ fontStyle: 'italic', color: '#999' }}>Sin comentario</span>}
                  </Typography>

                  <Box display="flex" justifyContent="flex-end">
                    <Chip label="Nueva reseña" size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, fontWeight: 'bold' }} />
                  </Box>
                </Box>

                <Divider />

                {/* Info Grid */}
                <Grid container p={3} pb={2} spacing={2}>
                  
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Fecha</Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                      <AccessTimeIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body2" fontWeight="bold" color="primary">{new Date(selectedReview.createTime).toLocaleDateString()} {new Date(selectedReview.createTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Estado</Typography>
                    <Box mt={0.5}>
                      {getStatusChip(selectedReview.estado)}
                    </Box>
                  </Grid>
                </Grid>

                {/* IA Suggested Reply */}
                <Box p={3} bgcolor={alpha(theme.palette.primary.main, 0.02)} borderTop="1px solid" borderBottom="1px solid" borderColor="divider">
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight="bold">Respuesta</Typography>
                      {/* <Chip icon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />} label="IA" size="small" sx={{ height: 20, bgcolor: '#F3E5F5', color: '#9C27B0', '& .MuiChip-icon': { color: '#9C27B0' } }} /> */}
                    </Box>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      startIcon={isGeneratingIA ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />} 
                      onClick={handleGenerateWithIA}
                      disabled={isGeneratingIA}
                      sx={{ 
                        color: '#9C27B0', 
                        borderColor: '#9C27B0',
                        textTransform: 'none',
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: '#7B1FA2',
                          bgcolor: alpha('#9C27B0', 0.04)
                        }
                      }}
                    >
                      {isGeneratingIA ? 'Generando...' : 'Generar con IA'}
                    </Button>
                  </Box>

                  <TextField
                    multiline
                    fullWidth
                    minRows={4}
                    maxRows={10}
                    placeholder="Escribe tu respuesta aquí o génerala con IA..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Box display="flex" justifyContent="flex-end" mt={0.5}>
                    <Typography variant="caption" color="text.secondary">{replyText.length}/4000</Typography>
                  </Box>

                  <Box display="flex" gap={2} mt={1}>
                    <Button 
                      variant="contained" 
                      endIcon={<SendIcon />} 
                      onClick={handleSendReply}
                      sx={{ flexGrow: 1, borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
                    >
                      {selectedReview.reviewReply ? 'Actualizar respuesta a Google' : 'Enviar respuesta a Google'}
                    </Button>
                    {/* {selectedReview.reviewReply && (
                      <Button variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', borderColor: 'divider', color: 'text.primary' }}>
                        Editar respuesta
                      </Button>
                    )} */}
                  </Box>
                </Box>

                {/* Quick Actions */}
                {/* <Box p={3}>
                  <Typography variant="subtitle2" fontWeight="bold" mb={2}>Acciones rápidas</Typography>
                  <Grid container spacing={2} textAlign="center">
                    <Grid size={{ xs: 2.4 }}>
                      <IconButton sx={{ bgcolor: alpha('#34A853', 0.1), color: '#34A853', mb: 1 }}>
                        <CheckCircleIcon />
                      </IconButton>
                      <Typography variant="caption" display="block" lineHeight={1.2}>Marcar como<br/>respondida</Typography>
                    </Grid>
                    <Grid size={{ xs: 2.4 }}>
                      <IconButton sx={{ bgcolor: alpha('#FBBC04', 0.1), color: '#F9A825', mb: 1 }}>
                        <FolderIcon />
                      </IconButton>
                      <Typography variant="caption" display="block" lineHeight={1.2}>Crear caso<br/>interno</Typography>
                    </Grid>
                    <Grid size={{ xs: 2.4 }}>
                      <IconButton sx={{ bgcolor: alpha('#1976D2', 0.1), color: '#1976D2', mb: 1 }}>
                        <PersonAddIcon />
                      </IconButton>
                      <Typography variant="caption" display="block" lineHeight={1.2}>Asignar<br/>responsable</Typography>
                    </Grid>
                    <Grid size={{ xs: 2.4 }}>
                      <IconButton sx={{ bgcolor: alpha('#EA4335', 0.1), color: '#EA4335', mb: 1 }}>
                        <TrendingUpIcon />
                      </IconButton>
                      <Typography variant="caption" display="block" lineHeight={1.2}>Escalar a<br/>Calidad</Typography>
                    </Grid>
                    <Grid size={{ xs: 2.4 }}>
                      <IconButton sx={{ bgcolor: alpha('#4285F4', 0.1), color: '#4285F4', mb: 1 }}>
                        <GoogleIcon />
                      </IconButton>
                      <Typography variant="caption" display="block" lineHeight={1.2}>Ver en<br/>Google</Typography>
                    </Grid>
                  </Grid>
                </Box> */}
              </CardContent>
            </Card>
          ) : (
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px dashed', borderColor: 'divider', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">Selecciona una reseña para ver el detalle</Typography>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};


export default Resumen;
